//-- Copyright (c) 2021 TMT Insights Group, LLC
import { Redirect, Route, useLocation } from 'react-router-dom';
import { selectIsAuthenticated, selectPermissions } from '@/redux/selectors/authSelectors';
import Layout from '../../layout/Layout/Layout';
import { findFirstAccessibleRoute } from '@/_utils/routes';
import { PolarisRoute } from '@/view/polarisRoutes';
import NoRoute from '@/components/NoRoute/NoRoute';
import { useAppSelector } from '@/redux/hooks';

type PrivateRouteProps = {
  component: any;
  role: PolarisRoute['role'];
  configOptions: { key: string; label: string }[];
  path: PolarisRoute['path'];
  exact?: PolarisRoute['exact'];
  calcTitle: PolarisRoute['calcTitle'];
  ignoreScrollBehavior?: PolarisRoute['ignoreScrollBehavior'];
};

interface LocationType {
  state?: {
    redirected?: boolean;
  };
}

function PrivateRoute({ component: Component, role, configOptions, calcTitle, ...rest }: PrivateRouteProps) {
  const location: LocationType = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const permissions = useAppSelector(selectPermissions);
  const userHasPermission = rest.path === '/no-access' || (role && permissions.includes(role));

  let alternateNoAccess = '/no-access';

  if (!userHasPermission) {
    const route = findFirstAccessibleRoute(permissions);

    if (route) {
      alternateNoAccess = route;
    }
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isAuthenticated) {
          return <Redirect to={{ pathname: '/login', state: { from: location } }} />;
        }

        if (!userHasPermission && !location.state?.redirected) {
          return <Redirect to={{ pathname: alternateNoAccess, state: { from: location, redirected: true } }} />;
        }

        return (
          <Layout calcTitle={calcTitle} configOptions={configOptions} {...props}>
            {!userHasPermission && location.state?.redirected ? <NoRoute /> : <Component {...props} />}
          </Layout>
        );
      }}
    />
  );
}

export default PrivateRoute;
